import React, { useEffect, useMemo, useRef, useState, useContext } from "react";
import { ArrowLeft } from "lucide-react";
import { useNavigate } from "react-router-dom";
import PaymentHistoryCard from "../Accounts/PaymentHistoryCard";

import { initiateRazorpayPayment } from "../../utils/razorpay";
import { AuthContext } from "../../context/AuthContext";
import {
  fetchResidentSubscription,
  fetchPaymentHistory,
} from "../../services/resident_api";

// Credit packages with different tiers
const creditPackages = [
  { credits: 3000, monthlyPrice: 99, yearlyPrice: 891, label: "3k" },
  { credits: 18000, monthlyPrice: 399, yearlyPrice: 3591, label: "18k" },
  { credits: 36000, monthlyPrice: 699, yearlyPrice: 6291, label: "36k" },
  { credits: 72000, monthlyPrice: 1199, yearlyPrice: 10791, label: "72k" },
  { credits: 21600, monthlyPrice: 0, yearlyPrice: 1599, label: "21.6k/year" },
];

// Marks for the slider
const marks = creditPackages.map((pkg, index) => ({
  value: index,
  label: pkg.label,
}));

const plans = [
  {
    id: "mini",
    name: "Mini",
    price: 1,
    duration: "Daily",
    badge: "Most Used",
    badgeColor: "bg-blue-600",
    bg: "bg-[#007FFF]",
  },
  {
    id: "plus",
    name: "Plus",
    price: 119,
    duration: "Weekly",
    badge: "Best Value",
    badgeColor: "bg-purple-600",
    bg: "bg-[#0039CB]",
  },
  {
    id: "pro",
    name: "Pro",
    price: 499,
    duration: "Monthly",
    badge: "Most Popular",
    badgeColor: "bg-green-600",
    bg: "bg-[#2E7D6E]",
  },
  {
    id: "premium",
    name: "Premium",
    price: 1599,
    duration: "Yearly",
    badge: "Top Choice",
    badgeColor: "bg-yellow-600",
    bg: "bg-[#FFA726]",
  },
];

// Map API plan_name → our local plan id
const planNameToId = (name) => {
  const s = (name || "").toLowerCase();
  if (["mini", "plus", "pro", "premium"].includes(s)) return s;
  if (s.includes("day")) return "mini";
  if (s.includes("week")) return "plus";
  if (s.includes("month")) return "pro";
  if (s.includes("year")) return "premium";
  return null;
};

// Add N days to a date
const addDays = (date, days) => {
  const d = new Date(date);
  d.setDate(d.getDate() + days);
  return d;
};

// Format yyyy-mm-dd → 11 Aug 2025
const fmt = (iso) =>
  iso
    ? new Date(iso).toLocaleDateString("en-GB", {
        day: "2-digit",
        month: "short",
        year: "numeric",
      })
    : "--";

const SubscriptionPage = (props) => {
  const navigate = useNavigate();
  const { user } = useContext(AuthContext);
  const accessToken =
    typeof window !== "undefined" ? localStorage.getItem("access_token") : null;
  const payInFlight = useRef(false);

  // Profile (from props + AuthContext)
  const [residentId, setResidentId] = useState(props.residentId ?? null);
  const [residentName, setResidentName] = useState(props.residentName ?? "");
  const [residentPhone, setResidentPhone] = useState(props.residentPhone ?? "");

  // Subscription state
  const [currentSub, setCurrentSub] = useState(null);
  const [subLoading, setSubLoading] = useState(false);
  const [subError, setSubError] = useState("");

  // Payment history state (✅ inside component; seeded from props if provided)
  const [paymentHistory, setPaymentHistory] = useState(
    props.paymentHistory ?? []
  );

  // UI state
  const [autoRenew, setAutoRenew] = useState(true);
  const [selectedPlanId, setSelectedPlanId] = useState("mini");

  // Credit scale state
  const [selectedCreditIndex, setSelectedCreditIndex] = useState(2); // Default to 36k (index 2)
  const [isYearlyBilling, setIsYearlyBilling] = useState(true);
  const [isDragging, setIsDragging] = useState(false);
  const [showValueLabel, setShowValueLabel] = useState(false);
  const sliderRef = useRef(null);
  const sliderHandleRef = useRef(null);

  const selectedCreditPackage = creditPackages[selectedCreditIndex];

  // Sync props → state
  useEffect(() => {
    if (props.residentId) setResidentId(props.residentId);
    if (props.residentName) setResidentName(props.residentName);
    if (props.residentPhone) setResidentPhone(props.residentPhone);
  }, [props.residentId, props.residentName, props.residentPhone]);

  // Fallback from AuthContext if props were empty
  useEffect(() => {
    if (!user) return;
    const id = user?.id || user?.member_id;
    const phone =
      user?.phone_number?.trim() || user?.member_data?.phone_number?.trim();
    const name = user?.name || user?.member_data?.name;
    if (id) setResidentId(id);
    if (name) setResidentName(name);
    if (phone) setResidentPhone(phone);
  }, [user]);

  // Handle mouse events for slider
  useEffect(() => {
    const handleMouseDown = (e) => {
      if (
        sliderHandleRef.current &&
        sliderHandleRef.current.contains(e.target)
      ) {
        setIsDragging(true);
        setShowValueLabel(true);
      }
    };

    const handleMouseMove = (e) => {
      if (!isDragging || !sliderRef.current) return;

      const slider = sliderRef.current;
      const rect = slider.getBoundingClientRect();
      let position = (e.clientX - rect.left) / rect.width;

      // Constrain position to 0-1 range
      position = Math.max(0, Math.min(1, position));

      // Calculate which segment to snap to
      const segmentWidth = 1 / (creditPackages.length - 1);
      const segmentIndex = Math.round(position / segmentWidth);

      // Ensure index is within bounds
      const newIndex = Math.min(
        Math.max(segmentIndex, 0),
        creditPackages.length - 1
      );
      setSelectedCreditIndex(newIndex);
    };

    const handleMouseUp = () => {
      setIsDragging(false);
    };

    document.addEventListener("mousedown", handleMouseDown);
    document.addEventListener("mousemove", handleMouseMove);
    document.addEventListener("mouseup", handleMouseUp);

    return () => {
      document.removeEventListener("mousedown", handleMouseDown);
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", handleMouseUp);
    };
  }, [isDragging]);

  // Handle touch events for mobile
  useEffect(() => {
    const handleTouchStart = (e) => {
      if (
        sliderHandleRef.current &&
        sliderHandleRef.current.contains(e.target)
      ) {
        setIsDragging(true);
        setShowValueLabel(true);
      }
    };

    const handleTouchMove = (e) => {
      if (!isDragging || !sliderRef.current) return;

      const slider = sliderRef.current;
      const rect = slider.getBoundingClientRect();
      let position = (e.touches[0].clientX - rect.left) / rect.width;

      // Constrain position to 0-1 range
      position = Math.max(0, Math.min(1, position));

      // Calculate which segment to snap to
      const segmentWidth = 1 / (creditPackages.length - 1);
      const segmentIndex = Math.round(position / segmentWidth);

      // Ensure index is within bounds
      const newIndex = Math.min(
        Math.max(segmentIndex, 0),
        creditPackages.length - 1
      );
      setSelectedCreditIndex(newIndex);
    };

    const handleTouchEnd = () => {
      setIsDragging(false);
    };

    document.addEventListener("touchstart", handleTouchStart);
    document.addEventListener("touchmove", handleTouchMove);
    document.addEventListener("touchend", handleTouchEnd);

    return () => {
      document.removeEventListener("touchstart", handleTouchStart);
      document.removeEventListener("touchmove", handleTouchMove);
      document.removeEventListener("touchend", handleTouchEnd);
    };
  }, [isDragging]);

  // auto-refresh
  useEffect(() => {
    loadSubscription(); // initial fetch

    // refresh when tab regains focus
    const onFocus = () => {
      console.log("🔁 Window focus — refreshing subscription");
      loadSubscription();
    };
    window.addEventListener("focus", onFocus);

    // auto-refresh every 5 seconds
    const interval = setInterval(() => {
      console.log("⏳ Auto-refresh subscription (every 5s)");
      loadSubscription();
    }, 5000);

    // cleanup
    return () => {
      window.removeEventListener("focus", onFocus);
      clearInterval(interval);
    };
  }, []);

  const isProfileComplete = useMemo(
    () => Boolean(residentId && residentName?.trim() && residentPhone?.trim()),
    [residentId, residentName, residentPhone]
  );

  // Fetch current subscription
  const loadSubscription = async () => {
    setSubLoading(true);
    setSubError("");
    try {
      const resp = await (fetchResidentSubscription?.length
        ? fetchResidentSubscription(accessToken)
        : fetchResidentSubscription());
      if (resp?.success) setCurrentSub(resp.data);
      else setSubError(resp?.message || "Failed to fetch subscription");
    } catch (err) {
      console.error("Subscription error:", err);
      setSubError("Failed to fetch subscription");
    } finally {
      setSubLoading(false);
    }
  };

  // ✅ helper: read either key ("access_token" or "access")
  const getAccessToken = () => {
    if (typeof window === "undefined") return null;
    return (
      localStorage.getItem("access_token") ||
      localStorage.getItem("access") ||
      null
    );
  };

  // Fetch payment history (no hard bail)
  const loadPaymentHistory = async () => {
    const token = getAccessToken();
    try {
      console.log(
        "🛰️ [SubscriptionPage] Fetching payment history… token?",
        !!token
      );
      const resp = await fetchPaymentHistory(token); // token may be null; that's fine
      if (resp?.success && Array.isArray(resp.data)) {
        let rows = resp.data;

        // Optional soft filter: only apply if it keeps at least one item
        const maybeFiltered = rows.filter(
          (r) =>
            (residentPhone &&
              String(r.resident_phone) === String(residentPhone)) ||
            (residentName &&
              r.resident_name?.trim?.() === residentName?.trim?.())
        );
        if (maybeFiltered.length > 0) rows = maybeFiltered;

        const mapped = rows.map((item) => ({
          planDuration: item.plan_name || "N/A",
          paymentDate: new Date(item.created_at).toLocaleString(),
          amount: Number(item.amount),
          planPrice: Number(item.amount),
          paymentMode: item.payment_id ? "Card" : "Unpaid",
          paymentId: item.payment_id || item.order_id,
        }));

        setPaymentHistory(mapped);
      } else {
        setPaymentHistory([]);
      }
    } catch (e) {
      console.error("🟥 Failed to fetch payment history:", e);
      setPaymentHistory([]);
    }
  };

  useEffect(() => {
    if (isProfileComplete) {
      loadSubscription();
      loadPaymentHistory();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isProfileComplete]);

  // Determine active plan from API
  const activePlanId = useMemo(
    () => planNameToId(currentSub?.plan_name),
    [currentSub?.plan_name]
  );

  // Auto-highlight active card
  useEffect(() => {
    if (activePlanId) setSelectedPlanId(activePlanId);
  }, [activePlanId]);

  // GLOBAL LOCK: disable all buttons until day after end_date
  const isLocked = useMemo(() => {
    if (!currentSub?.is_active) return false;
    const end = currentSub?.end_date ? new Date(currentSub.end_date) : null;
    if (!end) return true; // safe default
    const unlockAt = addDays(end, 1);
    return new Date() < unlockAt;
  }, [currentSub]);

  const handlePayment = async () => {
    if (payInFlight.current) return;

    if (!isProfileComplete) {
      alert(
        "Resident information is incomplete. Please update your profile details before proceeding with payment."
      );
      return;
    }
    if (isLocked) return;

    const selectedPlan = plans.find((p) => p.id === selectedPlanId);
    if (!selectedPlan) {
      alert("Please select a plan.");
      return;
    }

    try {
      payInFlight.current = true;
      await initiateRazorpayPayment({
        plan: selectedPlan,
        residentId,
        residentName,
        residentPhone,
        autoRenew,
      });
    } finally {
      payInFlight.current = false;
    }
  };

  const currentTitle = currentSub?.plan_name
    ? `${currentSub.plan_name.replace("-", " ")}`
    : "Mini (Daily)";

  return (
    <div className="min-h-screen bg-[#f8f9fa] flex flex-col">
      <header className="w-full bg-[#004990] px-4 py-4 relative z-50">
        <div className="max-w-6xl mx-auto flex items-center justify-between">
          <button
            onClick={() => navigate(-1)}
            className="text-white hover:bg-white/10 p-1 rounded-full"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <h1 className="text-white font-semibold text-lg">
            Subscription & Payment
          </h1>
        </div>
      </header>

      <div className="max-w-4xl mx-auto px-4 w-full mt-6">
        {/* Current Plan Card */}
        <div className="w-full bg-green-50 rounded-xl p-6 pt-4 shadow-md">
          <div className="flex items-start justify-between">
            <div>
              <h2 className="text-xl font-bold text-gray-800 mb-1">
                Current Plan
              </h2>
              <p className="text-base font-medium text-green-700">
                {currentTitle}{" "}
                {currentSub?.is_active === false ? "(Inactive)" : ""}
              </p>

              {/* ↓ Uncomment if you want to show name/phone under title */}
              {/*
              <p className="text-sm text-gray-600 mt-1">
                {residentName || "--"} • {residentPhone || "--"}
              </p>
              */}
            </div>

            {/* ↓ Auto-renew toggle kept but commented (as in your version) */}
            {/*
            <div className="flex items-center gap-2">
              <span className="text-sm font-medium text-gray-800">
                Auto renew
              </span>
              <button
                onClick={() => setAutoRenew((x) => !x)}
                className={`relative w-10 h-6 rounded-full transition-colors duration-300 ${
                  autoRenew ? "bg-green-500" : "bg-gray-300"
                }`}
              >
                <span
                  className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full shadow-md transform transition-transform duration-300 ${
                    autoRenew ? "translate-x-4" : ""
                  }`}
                />
              </button>
            </div>
            */}
          </div>

          <div className="flex flex-col sm:flex-row justify-between text-base text-gray-700 mt-2 gap-1 sm:gap-0">
            <p>
              <span className="font-semibold">Begins:</span>{" "}
              {subLoading ? "Loading..." : fmt(currentSub?.start_date) || "—"}
            </p>
            <p>
              <span className="font-semibold">Ends:</span>{" "}
              {subLoading ? "Loading..." : fmt(currentSub?.end_date) || "—"}
            </p>
          </div>

          {subError && (
            <p className="text-sm text-red-600 mt-2">⚠️ {subError}</p>
          )}

          {/* ↓ Manual refresh kept but commented (as in your version) */}
          {/*
          <button
            onClick={loadSubscription}
            className="mt-3 text-sm text-blue-700 underline"
            disabled={subLoading}
          >
            {subLoading ? "Refreshing…" : "Refresh subscription"}
          </button>
          */}
        </div>

        {/* Credit Scale Section */}
        <div className="w-full mt-8 bg-white rounded-xl p-6 shadow-md">
          <h2 className="text-xl font-bold text-gray-800 mb-2 text-center">
            Choose Your Credit Package
          </h2>
          <p className="text-sm text-gray-600 mb-6 text-center">
            {isYearlyBilling ? "Pay yearly (25% off)" : "Pay monthly"}
          </p>

          {/* Credit counter display */}
          <div className="flex justify-center items-center mb-6">
            <div className="bg-blue-50 border border-blue-200 rounded-lg px-6 py-3">
              <span className="text-2xl font-bold text-blue-700">
                {selectedCreditPackage.credits.toLocaleString()} credits
              </span>
            </div>
          </div>

          {/* Slider with marks and value label */}
          <div className="mb-8 relative">
            {/* Value label that appears on hover */}
            <div
              className={`absolute -top-8 left-1/2 transform -translate-x-1/2 -translate-y-full bg-gray-800 text-white text-xs font-medium px-2 py-1 rounded-md transition-opacity duration-200 ${
                showValueLabel ? "opacity-100" : "opacity-0 pointer-events-none"
              }`}
              style={{
                left: `${
                  (selectedCreditIndex / (creditPackages.length - 1)) * 100
                }%`,
              }}
            >
              {selectedCreditPackage.credits.toLocaleString()} credits
              <div className="absolute bottom-0 left-1/2 transform -translate-x-1/2 translate-y-full w-2 h-2 bg-gray-800 rotate-45"></div>
            </div>

            <div
              ref={sliderRef}
              className="relative h-2 bg-gray-200 rounded-full"
            >
              {/* Filled portion */}
              <div
                className="absolute top-0 left-0 h-full bg-blue-600 rounded-full"
                style={{
                  width: `${
                    (selectedCreditIndex / (creditPackages.length - 1)) * 100
                  }%`,
                }}
              />

              {/* Marks */}
              {creditPackages.map((pkg, index) => (
                <div
                  key={index}
                  className="absolute top-1/2 transform -translate-y-1/2 -translate-x-1/2"
                  style={{
                    left: `${(index / (creditPackages.length - 1)) * 100}%`,
                  }}
                >
                  <div
                    className={`w-4 h-4 rounded-full border-4 ${
                      index <= selectedCreditIndex
                        ? "border-blue-600 bg-white"
                        : "border-gray-300 bg-white"
                    }`}
                  />
                </div>
              ))}

              {/* Slider handle with cursor */}
              <div
                ref={sliderHandleRef}
                className="absolute top-1/2 w-6 h-6 bg-white border-4 border-blue-600 rounded-full transform -translate-y-1/2 -translate-x-1/2 shadow-lg cursor-grab active:cursor-grabbing"
                style={{
                  left: `${
                    (selectedCreditIndex / (creditPackages.length - 1)) * 100
                  }%`,
                  zIndex: 10,
                }}
                onMouseEnter={() => setShowValueLabel(true)}
                onMouseLeave={() => !isDragging && setShowValueLabel(false)}
              >
                {/* Pointer line extending downward */}
                <div className="absolute top-full left-1/2 w-0.5 h-4 bg-blue-600 transform -translate-x-1/2" />
              </div>
            </div>

            {/* Mark labels */}
            <div className="flex justify-between mt-4">
              {creditPackages.map((pkg, index) => (
                <div
                  key={index}
                  className={`text-xs font-medium ${
                    index === selectedCreditIndex
                      ? "text-blue-600 font-bold"
                      : "text-gray-500"
                  }`}
                  style={{
                    marginLeft: index === 0 ? "0" : "-8px",
                    marginRight:
                      index === creditPackages.length - 1 ? "0" : "-8px",
                  }}
                >
                  {pkg.label}
                </div>
              ))}
            </div>
          </div>

          {/* Billing toggle */}
          <div className="flex justify-center gap-4 mb-6">
            <button
              onClick={() => setIsYearlyBilling(true)}
              className={`px-4 py-2 rounded-lg font-medium ${
                isYearlyBilling
                  ? "bg-blue-600 text-white shadow-md"
                  : "bg-gray-100 text-gray-700"
              }`}
            >
              Pay Yearly (25% off)
            </button>
            <button
              onClick={() => setIsYearlyBilling(false)}
              className={`px-4 py-2 rounded-lg font-medium ${
                !isYearlyBilling
                  ? "bg-blue-600 text-white shadow-md"
                  : "bg-gray-100 text-gray-700"
              }`}
            >
              Pay Monthly
            </button>
          </div>

          {/* Price and CTA */}
          <div className="text-center">
            <p className="text-2xl font-bold text-gray-800 mb-2">
              {isYearlyBilling
                ? `₹${selectedCreditPackage.yearlyPrice}/year`
                : `₹${selectedCreditPackage.monthlyPrice}/month`}
            </p>
            {isYearlyBilling && selectedCreditPackage.monthlyPrice > 0 && (
              <p className="text-sm text-gray-500 mb-4">
                <s>₹{selectedCreditPackage.monthlyPrice * 12}/year</s> (Save
                25%)
              </p>
            )}
            <button
              onClick={handlePayment}
              disabled={!isProfileComplete || isLocked}
              className="px-8 py-3 bg-blue-600 text-white font-semibold rounded-lg hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed shadow-md"
            >
              Get Started
            </button>
          </div>
        </div>

        {/* Plans */}
        <div className="w-full mt-8 grid grid-cols-2 sm:grid-cols-4 gap-4 sm:gap-6">
          {plans.map((plan) => {
            const isSelected = selectedPlanId === plan.id;
            const planBorder = `border-2 ${
              isSelected ? plan.bg.replace("bg-", "border-") : "border-gray-300"
            }`;

            // (not strictly needed with global lock, but harmless to keep)
            const isThisPlanActive = Boolean(
              currentSub?.is_active && activePlanId === plan.id
            );

            return (
              <div
                key={plan.id}
                onClick={() => setSelectedPlanId(plan.id)}
                className={`relative rounded-xl ${
                  isSelected ? "h-[210px]" : "h-[150px]"
                } p-5 shadow-md cursor-pointer transition ${
                  isSelected
                    ? `${plan.bg} text-white`
                    : `bg-white ${plan.bg.replace("bg-", "text-")}`
                } ${planBorder}`}
              >
                <div className="absolute -top-2 left-3 z-20">
                  <span
                    className={`text-xs px-3 py-1 rounded-lg font-semibold uppercase ${plan.badgeColor} text-white`}
                  >
                    {plan.badge}
                  </span>
                </div>
                <div className="relative z-10 text-center">
                  <p className="font-bold text-lg mt-2">{plan.name}</p>
                  <p className="text-3xl font-bold mt-2">₹{plan.price}</p>
                  <p className="text-sm">{plan.duration}</p>

                  {/* Button (design unchanged) */}
                  {isSelected && (
                    <button
                      onClick={handlePayment}
                      // GLOBAL LOCK APPLIED HERE
                      disabled={!isProfileComplete || isLocked}
                      title={
                        isLocked
                          ? "You can select a new plan the day after the current plan ends."
                          : !isProfileComplete
                          ? "Complete your profile to proceed"
                          : ""
                      }
                      className={`mt-6 text-sm font-semibold px-5 py-1.5 rounded-lg border ${
                        !isProfileComplete || isLocked
                          ? "bg-gray-200 text-gray-400 border-gray-300 cursor-not-allowed"
                          : "bg-white text-gray-700 border-gray-400 hover:bg-gray-100"
                      }`}
                    >
                      Selected
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>

        {/* Features */}
        <div className="w-full mt-6 bg-gray-200 rounded-xl shadow-md p-5">
          <h2 className="text-lg font-semibold text-gray-800 mb-4">
            Features:
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-gray-700 text-sm">
            <div>✅ 24 / 7 Access</div>
            <div>✅ Security Measures (CCTV, OTP)</div>
            <div>✅ Pre-Approval</div>
            <div>✅ Energy-Efficient Design</div>
            <div>✅ UV Sanitization</div>
            <div>✅ Maintenance And Support</div>
          </div>
        </div>

        {/* Payment History */}
        <div className="w-full bg-gray-200 rounded-xl shadow-md p-6 mt-6">
          <h2 className="text-lg font-bold text-gray-800 mb-4">
            Payment History
          </h2>
          {(paymentHistory || []).length > 0 ? (
            paymentHistory.map((entry, index) => (
              <PaymentHistoryCard key={index} data={entry} />
            ))
          ) : (
            <p className="text-gray-600">No payment history available.</p>
          )}
        </div>

        {/* Footer */}
        <div className="w-full mt-10 mb-6 flex justify-center gap-6 text-sm text-gray-500">
          <button className="hover:underline">Privacy Policy</button>
          <button className="hover:underline">FAQ</button>
          <button className="hover:underline">Help Center</button>
        </div>
      </div>
    </div>
  );
};

export default SubscriptionPage;
